import { useTranslation } from "react-i18next";
import { Color } from "config/colors";
import "react-tooltip/dist/react-tooltip.css";
import {
  Text,
  ClockStampCreateAutoGeneratedGigBody,
  ButtonContainer,
} from "./clockStamp.styled";
import { FontFamily, FontSize } from "config/font";
import { Modal } from "@material-ui/core";
import { TimeReportForStamping } from "model/Company";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useAlertStore } from "stores/alertStore/alertStore";
import {
  DropdownOption,
  DropdownWithLabel,
} from "../general/dropDownWithLabel/dropdownWithLabel.component";
import { useEffect, useMemo, useState } from "react";
import { StampClockTeam } from "model/OrganizationalStructure";
import { TimePickerWithLabel } from "../general/timePicker/timePickerWithLabel.component";
import { ButtonComponent } from "../general/button/button.component";
import { TextInputWithLabel } from "../general/textInput/textInput.component";

type Props = {
  setShowCreateAutoGeneratedGigModal: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setKeyShouldBeDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  showCreateAutoGeneratedGigModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setTimeReport: React.Dispatch<
    React.SetStateAction<TimeReportForStamping | undefined>
  >;
  setIsAutoGeneratedGig: React.Dispatch<React.SetStateAction<boolean>>;
  clearInputs: any;
  teams: StampClockTeam[];
  phoneNumber: string | undefined;
};

const extractTimeFromISO = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  } catch {
    return "";
  }
};

const parseHHMMToMinutes = (time: string): number => {
  const [hStr, mStr] = time.split(":");
  const hours = Number(hStr);
  const minutes = Number(mStr);
  if (isNaN(hours) || isNaN(minutes)) return 0;
  return hours * 60 + minutes;
};

const getPlannedMinutesFromCompanySettings = (
  startTime: string,
  endTime: string
): number => {
  const startMinutes = parseHHMMToMinutes(startTime);
  const endMinutes = parseHHMMToMinutes(endTime);

  let diff = endMinutes - startMinutes;
  if (diff < 0) {
    diff += 24 * 60;
  }

  return diff;
};

const addMinutesToIso = (iso: string, minutes: number): string => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  date.setMinutes(date.getMinutes() + minutes);
  return date.toISOString();
};

const nowISO = () => {
  const now = new Date();
  return now.toISOString();
};

const buildEndTimeFromTimeOfDay = (
  startIso: string,
  newEndIso: string
): string => {
  const startDate = new Date(startIso);
  const newEndDate = new Date(newEndIso);

  if (isNaN(startDate.getTime()) || isNaN(newEndDate.getTime())) {
    return newEndIso;
  }

  const startHours = startDate.getHours();
  const startMinutes = startDate.getMinutes();

  const newEndHours = newEndDate.getHours();
  const newEndMinutes = newEndDate.getMinutes();

  const result = new Date(startDate);
  result.setHours(newEndHours, newEndMinutes, 0, 0);

  if (
    newEndHours < startHours ||
    (newEndHours === startHours && newEndMinutes < startMinutes)
  ) {
    result.setDate(result.getDate() + 1);
  }

  return result.toISOString();
};

export const ClockStampCreateAutoGeneratedGigModal: React.FC<Props> = ({
  setShowCreateAutoGeneratedGigModal,
  setKeyShouldBeDisabled,
  showCreateAutoGeneratedGigModal,
  setShowModal,
  setTimeReport,
  setIsAutoGeneratedGig,
  clearInputs,
  teams,
  phoneNumber,
}) => {
  const { t } = useTranslation();
  const [companyState, companyActions] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();

  const defaultStartTime =
    companyState.companySettings?.find(
      (s: any) => s.key === "CompanyGigShiftStartTime"
    )?.value ?? "08:00";

  const defaultEndTime =
    companyState.companySettings?.find(
      (s: any) => s.key === "CompanyGigShiftEndTime"
    )?.value ?? "17:00";

  const defaultBreakMinutesString =
    companyState.companySettings?.find((s: any) => s.key === "GigBreakTime")
      ?.value ?? "0";

  const plannedMinutes = useMemo(
    () =>
      getPlannedMinutesFromCompanySettings(defaultStartTime, defaultEndTime),
    [defaultStartTime, defaultEndTime]
  );

  const initialNow = useMemo(() => nowISO(), []);

  const [startTime, _setStartTime] = useState<string>(() => initialNow);
  const [endTime, setEndTime] = useState<string>(() =>
    addMinutesToIso(initialNow, plannedMinutes)
  );

  const validateStartTime = (isoString: string): string | null => {
    const timeString = extractTimeFromISO(isoString);
    if (!timeString) return "Invalid time format";

    const now = new Date();
    const [hours, minutes] = timeString.split(":").map(Number);

    const selectedTime = new Date();
    selectedTime.setHours(hours, minutes, 0, 0);

    const oneHourBefore = new Date(now.getTime() - 60 * 60 * 1000);
    const oneHourAfter = new Date(now.getTime() + 60 * 60 * 1000);

    if (selectedTime < oneHourBefore || selectedTime > oneHourAfter) {
      return t("StampClock.StartTimeMustBeWithinOneHour");
    }

    return null;
  };

  const validateEndTime = (
    startIsoString: string,
    endIsoString: string
  ): string | null => {
    const startTimeString = extractTimeFromISO(startIsoString);
    const endTimeString = extractTimeFromISO(endIsoString);

    if (!startTimeString || !endTimeString) return "Invalid time format";

    const [startHours, startMinutes] = startTimeString.split(":").map(Number);
    const [endHours, endMinutes] = endTimeString.split(":").map(Number);

    const startDate = new Date();
    startDate.setHours(startHours, startMinutes, 0, 0);

    const endDate = new Date();
    endDate.setHours(endHours, endMinutes, 0, 0);

    if (endDate <= startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }

    const timeDifferenceHours =
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);

    if (timeDifferenceHours > 14) {
      return t("StampClock.EndTimeMustBeWithin14Hours");
    }

    if (timeDifferenceHours <= 0) {
      return t("StampClock.EndTimeMustBeAfterStartTime");
    }

    return null;
  };

  const startTimeError = validateStartTime(startTime);
  const endTimeError = validateEndTime(startTime, endTime);
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const handleCloseModal = () => {
    setShowCreateAutoGeneratedGigModal(false);
    setKeyShouldBeDisabled(false);
    clearInputs();
  };

  const teamOptions: DropdownOption[] = useMemo(() => {
    return (
      teams?.map((team) => ({
        value: team.workGroupId.toString(),
        label: team.workGroupName.toString(),
      })) || []
    );
  }, [teams]);

  const [chosenTeam, setChosenTeam] = useState<DropdownOption | undefined>(
    teamOptions.length === 1 ? teamOptions[0] : undefined
  );

  const autoGenerateGig = () => {
    const data = {
      WorkGroupId: Number(chosenTeam?.value),
      StartTime: startTime,
      EndTime: endTime,
      breakTime: Number(defaultBreakMinutesString),
    };

    if (companyState.company?.id && phoneNumber) {
      setIsLoading(true);
      companyActions
        .createAutoGeneratedGig(
          companyState.company.id,
          phoneNumber.substring(1),
          data
        )
        .then((res) => {
          setTimeReport(res);
          setIsAutoGeneratedGig(true);
          clearInputs();
          setShowCreateAutoGeneratedGigModal(false);
          setShowModal(true);
          setIsLoading(false);
        })
        .catch((err: any) => {
          const msg = err?.response?.data?.errorMessage;

          if (msg === "Overlapping with existing gig") {
            setError(t("StampClock.OverlappingWithExistingGig"));
          } else {
            alertDispatch({
              type: "SHOW_ALERT",
              payload: {
                icon: "alert",
                title: t("Alerts.OhNo"),
                message: t("Alerts.SomethingWentWrong"),
              },
            });
          }
          setIsLoading(false);
          console.error("Error when trying to auto generate shift", err);
        });
    }
  };

  const validateData = () => {
    if (
      startTime &&
      endTime &&
      chosenTeam &&
      !startTimeError &&
      !endTimeError
    ) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    setError(undefined);
  }, [startTime, endTime]);

  return (
    <>
      <Modal
        open={showCreateAutoGeneratedGigModal}
        onClose={handleCloseModal}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <ClockStampCreateAutoGeneratedGigBody>
          <Text
            fontFamily={FontFamily.MontserratRegular}
            fontSize={FontSize.H3}
            color={Color.MidnightBlue}
          >
            {t("StampClock.CreateGig")}
          </Text>
          <Text
            fontFamily={FontFamily.MontserratRegular}
            fontSize={FontSize.Standard}
            color={Color.MidnightBlue}
          >
            {t("StampClock.AutoGenerateGigInfo")}
          </Text>
          {error && (
            <Text
              color={Color.LighterDestructive}
              fontSize={FontSize.Small}
              fontFamily={FontFamily.MontserratRegular}
            >
              {error}
            </Text>
          )}
          <div style={{ display: "flex", flexDirection: "row" }}>
            <TimePickerWithLabel
              label={t("StampClock.StartTime")}
              value={startTime}
              onChange={() => {}}
              disabled
            />
            <TimePickerWithLabel
              label={t("StampClock.EndTime")}
              value={endTime}
              onChange={(value: string) =>
                setEndTime(buildEndTimeFromTimeOfDay(startTime, value))
              }
            />
          </div>
          {endTimeError && (
            <Text
              color={Color.LighterDestructive}
              fontSize={FontSize.Small}
              fontFamily={FontFamily.MontserratRegular}
            >
              {endTimeError}
            </Text>
          )}
          <div style={{ display: "flex", flexDirection: "row" }}>
            <TextInputWithLabel
              label={t("StampClock.Break")}
              value={defaultBreakMinutesString}
              disabled
            />
            <DropdownWithLabel
              label={t("StampClock.Workgroup")}
              options={teamOptions}
              value={chosenTeam?.value}
              onChange={(value) => {
                const option = teamOptions.find((o) => o.value === value);
                setChosenTeam(option);
              }}
              placeholder={t("StampClock.ChooseWorkgroup")}
              required
            />
          </div>
          <ButtonContainer>
            <ButtonComponent
              size={"medium"}
              onClick={handleCloseModal}
              marginTop={10}
              marginBottom={20}
              marginRight={10}
              disabled={isLoading}
            >
              {t("StampClock.Close")}
            </ButtonComponent>
            <ButtonComponent
              action
              onClick={autoGenerateGig}
              disabled={!validateData() || isLoading}
              marginTop={10}
              marginBottom={20}
              marginRight={10}
            >
              {t("StampClock.Create")}
            </ButtonComponent>
          </ButtonContainer>
        </ClockStampCreateAutoGeneratedGigBody>
      </Modal>
    </>
  );
};
